/**
 * The two pieces every list screen is built from.
 *
 * `SidePanel` opens beside the list: one record's detail and the actions on
 * it, or a form that is not daily work. Escape and the backdrop close it.
 * `wide` is for a form with a line table, which needs more than a drawer's
 * reading width. `foot` is the bar the record's actions sit in.
 *
 * `Tabs` is the segmented control across the top of a list card, with a
 * count on each tab.
 */

import {
  useEffect,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react'

export function SidePanel({
  kicker,
  title,
  sub,
  amount,
  badge,
  wide,
  foot,
  onClose,
  children,
}: {
  kicker?: string
  title: ReactNode
  sub?: ReactNode
  amount?: string
  badge?: ReactNode
  wide?: boolean
  foot?: ReactNode
  onClose: () => void
  children: ReactNode
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <aside
        className={wide ? 'drawer wide' : 'drawer'}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : kicker}
      >
        <header className="drawer-head">
          <div>
            {kicker && <div className="appr-kind">{kicker}</div>}
            <h2>{title}</h2>
            {sub && <div className="muted">{sub}</div>}
          </div>
          {(amount || badge) && (
            <div className="drawer-amount">
              {amount && <div className="value">{amount}</div>}
              {badge}
            </div>
          )}
          <button className="quiet close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </header>
        <div className="drawer-body panel-body">{children}</div>
        {foot && <footer className="drawer-foot drawer-actions">{foot}</footer>}
      </aside>
    </>
  )
}

export interface TabSpec<K extends string> {
  key: K
  label: string
  count?: number
  //: Show the count only when there is something to look at.
  quietWhenZero?: boolean
}

export function Tabs<K extends string>({
  tabs,
  value,
  onChange,
}: {
  tabs: TabSpec<K>[]
  value: K
  onChange: (key: K) => void
}) {
  return (
    <div className="seg" role="tablist">
      {tabs.map((t) => (
        <button
          key={t.key}
          role="tab"
          aria-selected={value === t.key}
          className={value === t.key ? 'on' : undefined}
          onClick={() => onChange(t.key)}
        >
          {t.label}
          {t.count !== undefined && (!t.quietWhenZero || t.count > 0) && (
            <span className="count">{t.count}</span>
          )}
        </button>
      ))}
    </div>
  )
}

//: Click or Enter/Space opens a row; a button inside the row does its own job.
export function rowProps(onOpen: () => void, selected: boolean) {
  return {
    className: `appr-row${selected ? ' selected' : ''}`,
    tabIndex: 0,
    onClick: (e: ReactMouseEvent) => {
      if ((e.target as HTMLElement).closest('button, a, input, select, textarea, label')) return
      onOpen()
    },
    onKeyDown: (e: ReactKeyboardEvent) => {
      if (e.target !== e.currentTarget) return
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onOpen()
      }
    },
  }
}

//: Case-insensitive match of a search box against a row's text fields.
export function matches(needle: string, ...fields: (string | number | null | undefined)[]) {
  const n = needle.trim().toLowerCase()
  if (!n) return true
  return fields.some((f) => f !== null && f !== undefined && String(f).toLowerCase().includes(n))
}
